import React from 'react';

type InlineEditableTimelineFieldProps = {
    displayValue: string;
    editValue: string;
    inputType?: 'text' | 'datetime-local';
    inputStep?: number;
    ariaLabel: string;
    displayStyle?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
    onSave: (draft: string) => Promise<void>;
    saveErrorMessage: string;
};

export function InlineEditableTimelineField(props: InlineEditableTimelineFieldProps) {
    const {
        displayValue,
        editValue,
        inputType = 'text',
        inputStep,
        ariaLabel,
        displayStyle,
        inputStyle,
        onSave,
        saveErrorMessage,
    } = props;
    const [isEditing, setIsEditing] = React.useState(false);
    const [draft, setDraft] = React.useState(editValue);
    const [isSaving, setIsSaving] = React.useState(false);
    const isSavingRef = React.useRef(false);
    const cancelNextBlurRef = React.useRef(false);
    const restoreKeyboardFocusRef = React.useRef(false);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const displayButtonRef = React.useRef<HTMLButtonElement | null>(null);

    React.useEffect(() => {
        // Return keyboard focus after Enter/Escape, or back to the editor after a failed save.
        if (!restoreKeyboardFocusRef.current || isSaving) {
            return;
        }

        if (isEditing) {
            inputRef.current?.focus();
        } else {
            displayButtonRef.current?.focus();
        }
        restoreKeyboardFocusRef.current = false;
    }, [isEditing, isSaving]);

    const startEditing = React.useCallback(() => {
        // Refresh the draft from the latest saved value each time the editor opens.
        cancelNextBlurRef.current = false;
        restoreKeyboardFocusRef.current = false;
        setDraft(editValue);
        setIsEditing(true);
    }, [editValue]);

    const saveDraft = React.useCallback(async () => {
        // Blur is also emitted after keyboard actions, so guard cancellation and duplicate saves.
        if (cancelNextBlurRef.current) {
            cancelNextBlurRef.current = false;
            return;
        }
        if (isSavingRef.current) {
            return;
        }

        isSavingRef.current = true;
        setIsSaving(true);
        try {
            await onSave(draft);
            setIsEditing(false);
        } catch (error) {
            console.error('INLINE_EDITABLE_TIMELINE_FIELD: Could not save field:', error);
            window.alert(error instanceof Error ? error.message : saveErrorMessage);
        } finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    }, [draft, onSave, saveErrorMessage]);

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                type={inputType}
                step={inputStep}
                value={draft}
                onChange={(event) => setDraft(event.currentTarget.value)}
                onBlur={() => void saveDraft()}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                        event.preventDefault();
                        restoreKeyboardFocusRef.current = true;
                        event.currentTarget.blur();
                    }

                    if (event.key === 'Escape') {
                        event.preventDefault();
                        cancelNextBlurRef.current = true;
                        restoreKeyboardFocusRef.current = true;
                        setIsEditing(false);
                    }
                }}
                onFocus={(event) => event.currentTarget.select()}
                disabled={isSaving}
                autoFocus
                aria-label={ariaLabel}
                style={inputStyle}
            />
        );
    }

    return (
        <button
            ref={displayButtonRef}
            type='button'
            onDoubleClick={startEditing}
            onClick={(event) => {
                // Keyboard and assistive-technology button activation reports detail 0.
                if (event.detail === 0) {
                    startEditing();
                }
            }}
            onKeyDown={(event) => {
                if (event.key === 'F2') {
                    event.preventDefault();
                    startEditing();
                }
            }}
            aria-label={`Edit ${ariaLabel}: ${displayValue}`}
            title={`Double-click to edit ${ariaLabel.toLowerCase()}`}
            style={displayStyle}
        >
            {displayValue}
        </button>
    );
}
